import type {ChangeEvent, ReactElement} from 'react';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import type {IsUserEligibleResponse} from '@Components/social-media/social-media.types';
import {SocialPlatform, SocialPostCaptionOverwriteEvent} from '@Components/social-media/social-media.types';
import type {SocialAccount} from '@Components/social-media/account.vo';
import useOutsideClick from '@Hooks/useOutsideClick';
import {LabelledCheckbox} from '@Components/labelled-checkbox';
import {useAllPublishingParamsForAccount, useCommonCaption, useHasCommonCaption} from '@Components/social-media/social-media.hooks';
import {
  updateCommonCaption,
  updateHasCommonCaption,
  updatePublishingParamAllCaptions,
  updatePublishingParamCaption,
} from '@Components/social-media-wizard/social-media-wizard-reducer';
import {Text, TextSize, TextType} from '@Components/text';
import {Button, Size, Type} from '@Components/button';
import {captionCharacterLimits, CaptionErrorTypes} from '@Components/social-media-wizard/social-media-wizard.types';
import {
  getPostId,
  useAreCaptionsBeingAutoGenerated,
  useIsSocialMediaWizardAiCaptionsEnabled,
  usePlatformSelectionforAccount,
  useWizardAccountsToPublishTo,
  useWizardCaptionErrors,
} from '@Components/social-media-wizard/social-media-wizard.hooks';
import {AiButton} from '@Components/ai-button';
import {useIsMobileWidth} from '@Hooks/useIsMobileWidth';
import {IntroPopup} from '@Components/intro-popup';
import {getAssetUrl} from '@Utils/s3.util';
import {GA4EventName} from '@Libraries/ga-events';
import {isUserPremium} from '@Libraries/user.library';
import {AnimatePresence, motion} from 'framer-motion';
import useAutoCaptionGlobal from '@Hooks/auto-captions/useAutoCaptionGlobal';
import useHasSeenAiCaptionIntroPopup from '@Hooks/auto-captions/useHasSeenAiCaptionIntroPopup';
import {EmojiPickerInput} from '@Components/emoji-picker-input';
import {AIButtonShape, AIButtonVariant} from '@Components/ai-button/ai-button.types';
import styles from './social-post-caption.module.scss';
import {useAppDispatch} from '@/hooks';
import useSelectCaption from '@Hooks/auto-captions/useSelectCaption';
import {CaptionWriterPopoverView} from './components/caption-writer-popover-view/caption-writer-popover-view';
import {
  CAPTION_TAGS_POPOVER_ALREADY_SHOWN,
  getRandomizedLoadingCaption,
  USER_HAS_SEEN_MAX_TAGS_POPOVERS,
} from '@Components/social-media-wizard/components/social-post-caption/social-post-caption.types';
import {getNonStoryPublishingParams, isCaptionTagsHighlightingEnabled} from '@Libraries/social-media';
import {Popover} from '@Components/popover';
import {PopoverDefaultAlignment} from '@Components/popover/popover.types';

interface SocialPostCaptionProps {
  account: SocialAccount;
}

const WAIT_TIME_BEFORE_AI_CAPTION_BUTTON_PULSATES_SECS = 10000; // 10 seconds
const WAIT_TIME_BEFORE_AI_CAPTION_REMINDER_POPOVER_IS_VISIBLE = 20000; // 20 seconds

function SocialPostCaption({account}: SocialPostCaptionProps): ReactElement<SocialPostCaptionProps> | null {
  const [isShowingConfirmation, setIsShowingConfirmation] = useState(false);
  const [isEmojiSelectorVisible, setIsEmojiSelectorVisible] = useState(false);
  const [isCountErrorVisible, setIsCountErrorVisible] = useState(false);
  const [isHashtagErrorVisible, setIsHashtagErrorVisible] = useState(false);
  const [isCaptionChangeMultiCharacter, setIsCaptionChangeMultiCharacter] = useState(false);
  const [showTagsPopOver, setShowTagsPopOver] = useState<boolean>(false);
  const [isUserEligibleToSeeTagsPopOver, setIsUserEligibleToSeeTagsPopOver] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const allPublishingParamsForAccount = useAllPublishingParamsForAccount(account.id);
  const publishingParamsForAccount = allPublishingParamsForAccount ? getNonStoryPublishingParams(allPublishingParamsForAccount) : null;
  const commonCaption = useCommonCaption();
  const hasCommonCaption = useHasCommonCaption();
  const emojiPickerRef = useRef<HTMLDivElement>(null);
  const iconRef = useRef<HTMLElement>(null);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const textAreaOverlayRef = useRef<HTMLDivElement>(null);
  const textAreaContainerRef = useRef<HTMLDivElement>(null);
  const captionErrors = useWizardCaptionErrors();
  const accountIdsToPublishTo = useWizardAccountsToPublishTo();
  const {setInAutoCaptionMode, closeAutoCaptionMode} = useAutoCaptionGlobal();
  const {hasSeenIntroPopup: userHasSeenAiCaptionPopup, setHasSeenIntroPopup, hasSeenReminderPopup, setHasSeenReminderPopup} = useHasSeenAiCaptionIntroPopup();
  const isMobile = useIsMobileWidth();
  const CAPTION_TEXT_BOX_HEIGHT = 220;
  const CAPTION_CONTAINER_TEXTBOX_HEIGHT = 245;
  const AUTO_CAPTION_BOX_HEIGHT = 280;
  const socialPostCaptionContainerRef = useRef<HTMLDivElement>(null);
  const aiCaptionButtonRef = useRef<HTMLButtonElement>(null);
  const [isAiCaptionButtonPulsating, setIsAiCaptionButtonPulsating] = useState<boolean>(false);
  const [isVisibleAiCaptionReminderPopup, setIsVisibleAiCaptionReminderPopup] = useState<boolean>(false);
  const isSocialMediaWizardAiCaptionsEnabled = useIsSocialMediaWizardAiCaptionsEnabled();
  const aiCaptionsPulseTimeoutRef = useRef<NodeJS.Timeout>(undefined);
  const aiCaptionReminderPopupTimeout = useRef<NodeJS.Timeout>(undefined);
  const platformSelection = usePlatformSelectionforAccount(account.id);
  const postId = getPostId();
  const {chooseCaption} = useSelectCaption();
  const areCaptionsBeingAutoGenerated = useAreCaptionsBeingAutoGenerated();
  const [autoGenerationLoadingCaption, setAutoGenerationLoadingCaption] = useState<string>('');
  const [isCaptionWriterOpen, setIsCaptionWriterOpen] = useState<boolean>(false);

  const getCurrentCaption = (): string => {
    if (hasCommonCaption && commonCaption) {
      return commonCaption;
    }

    if (!publishingParamsForAccount?.caption && textAreaRef.current) {
      return '';
    }
    return publishingParamsForAccount?.caption ?? '';
  };

  const captionCharCount = getCurrentCaption().length;

  useEffect(() => {
    const loadingStateCaption = getRandomizedLoadingCaption();
    setAutoGenerationLoadingCaption(loadingStateCaption);
    const captionOverwriteCallback = (_event: Event): void => {
      triggerTextAreaAnimation();
    };

    window.addEventListener(SocialPostCaptionOverwriteEvent, captionOverwriteCallback);

    return (): void => {
      window.removeEventListener(SocialPostCaptionOverwriteEvent, captionOverwriteCallback);
    };
  }, []);

  useEffect(() => {
    if (captionErrors !== undefined) {
      if (captionErrors[account.id] !== CaptionErrorTypes.NO_ERROR) {
        handleError();
      } else {
        setIsCountErrorVisible(false);
      }
    }
  }, [captionErrors]);

  useEffect(() => {
    const handleScroll = (): void => {
      if (textAreaOverlayRef.current && textAreaRef.current) {
        textAreaOverlayRef.current.scrollTop = textAreaRef.current.scrollTop;
      }
    };

    const textArea = textAreaRef.current;
    if (textArea) {
      textArea.addEventListener('scroll', handleScroll, {passive: true});
    }

    return (): void => {
      if (textArea) {
        textArea.removeEventListener('scroll', handleScroll);
      }
    };
  }, [textAreaRef.current, textAreaOverlayRef.current]);

  useEffect(() => {
    void checkCaptionTagsPopoverEligibility();
    setCaptionTagsPopoverUnseenInCache();
  }, []);

  const checkCaptionTagsPopoverEligibility = async (): Promise<void> => {
    const userHasSeenMaxTagsPopOvers = window.PMW.getCacheItem(USER_HAS_SEEN_MAX_TAGS_POPOVERS) as boolean;

    if (userHasSeenMaxTagsPopOvers) {
      setIsUserEligibleToSeeTagsPopOver(false);
    } else {
      const result = (await window.PMW.readLocal('socialmedia/shouldUserSeeCaptionTagsPopover')) as IsUserEligibleResponse;
      setIsUserEligibleToSeeTagsPopOver(result?.isEligible);

      if (!result?.isEligible) {
        window.PMW.setCacheItem(USER_HAS_SEEN_MAX_TAGS_POPOVERS, true, window.PMW.DEFAULT_CACHE_EXPIRY);
      }
    }
  };

  const setCaptionTagsPopoverUnseenInCache = (): void => {
    window.PMW.setCacheItem(CAPTION_TAGS_POPOVER_ALREADY_SHOWN, false);
  };

  const hasShortCaptionLimit = (accountType: SocialPlatform): boolean => {
    // Rather than this, can we not check if the captionCharacterLimits[accountType] <= 500?
    // 500 can be an exported constant from social-media-wizard.types.ts
    switch (accountType) {
      case SocialPlatform.THREADS:
      case SocialPlatform.TWITTER:
        return true;
      default:
        return false;
    }
  };

  const getCaptionPlaceholder = (): string => {
    switch (account.type) {
      case SocialPlatform.FACEBOOK:
      case SocialPlatform.FACEBOOK_PROFILE:
      case SocialPlatform.FACEBOOK_PAGE:
      case SocialPlatform.FACEBOOK_GROUP:
        return window.i18next.t('pmwjs_wizard_caption_placeholder_facebook');
      case SocialPlatform.TWITTER:
        return window.i18next.t('pmwjs_whats_happening');
      case SocialPlatform.LINKEDIN:
      case SocialPlatform.LINKEDIN_PAGE:
      case SocialPlatform.LINKEDIN_PROFILE:
        return window.i18next.t('pmwjs_what_to_talk_about');
      case SocialPlatform.TIKTOK:
        return window.i18next.t('pmwjs_describe_post_hashtags');
      case SocialPlatform.YOUTUBE:
        return window.i18next.t('pmwjs_add_a_caption_ellipses');
      case SocialPlatform.INSTAGRAM:
      case SocialPlatform.THREADS:
        return window.i18next.t('pmwjs_write_a_caption');
      case SocialPlatform.GOOGLE_BUSINESS_PROFILE:
        return window.i18next.t('pmwjs_post_update_for_x', {accountName: account.name});
      default:
        return '';
    }
  };

  const processCaptionToHighlightTags = (caption: string): string => {
    if (caption === '') {
      return `<span class="${styles.placeHolderText}">${getCaptionPlaceholder()}</span>`;
    }

    if (!isCaptionTagsHighlightingEnabled(account.type)) {
      return caption;
    }

    return caption.replace(/([@#][^\s,!?:&<>;"#']+)/g, (extractedWord) => {
      return `<span class="${styles.highlightTags}">${extractedWord}</span>`;
    });
  };

  const escapeHTMLCharactersFromCaption = (caption: string): string => {
    let updatedCaption = caption.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    if (updatedCaption.endsWith('\n')) {
      const htmlNewLineChar = '<br/>';
      updatedCaption += htmlNewLineChar;
    }
    return updatedCaption;
  };

  const currentCaptionAsHtml = useMemo(() => {
    const currentCaption: string = getCurrentCaption();
    const captionWithEscapedChars = escapeHTMLCharactersFromCaption(currentCaption);
    return processCaptionToHighlightTags(captionWithEscapedChars);
  }, [hasCommonCaption, commonCaption, publishingParamsForAccount?.caption]);

  const shouldShowIntroPopup = (): boolean => {
    if (isUserEligibleToSeeTagsPopOver) {
      return false;
    }

    if (isMobile) {
      return false;
    }

    if (userHasSeenAiCaptionPopup) {
      return false;
    }

    return accountIdsToPublishTo[0] === account.id;
  };

  const shouldShowReminderPopup = (): boolean => {
    if (isUserEligibleToSeeTagsPopOver) {
      return false;
    }

    if (!userHasSeenAiCaptionPopup) {
      return false;
    }

    if (hasSeenReminderPopup) {
      return false;
    }

    return accountIdsToPublishTo[0] === account.id;
  };

  const onCaptionWriterClose = (): void => {
    setIsCaptionWriterOpen(false);
    closeAutoCaptionMode();
  };

  const onAiCaptionClick = (viaOnboardingModal = false): void => {
    if (!postId) {
      return;
    }

    setInAutoCaptionMode({accountId: account.id});
    abortPulsatingButtonTimer();
    setIsAiCaptionButtonPulsating(false);

    if (!userHasSeenAiCaptionPopup) {
      setHasSeenIntroPopup(true);
    }

    if (userHasSeenAiCaptionPopup && !hasSeenReminderPopup && isVisibleAiCaptionReminderPopup) {
      onSeenReminderPopup();
    }

    setIsCaptionWriterOpen(true);

    if (viaOnboardingModal) {
      window.PMW.gtm.trackGA4CustomEvent(GA4EventName.SOCIALWIZARD_AICAPTIONS_ONBOARDING_TRYNOW);
    } else {
      if (isAiCaptionButtonPulsating) {
        window.PMW.gtm.trackGA4CustomEvent(GA4EventName.SOCIALWIZARD_AICAPTIONS_REMINDER_VISIBLE_CLICK);
      }

      window.PMW.gtm.trackGA4CustomEvent(GA4EventName.SOCIALWIZARD_AICAPTIONS_CLICKED);
    }
  };

  const afterCloseIntroPopup = (): void => {
    setHasSeenIntroPopup(true);
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.SOCIALWIZARD_AICAPTIONS_ONBOARDING_LATER);
  };

  const onEmojiPickerOutsideClick = (): void => {
    setIsEmojiSelectorVisible(false);
  };

  const startPulsatingButtonTimer = (): void => {
    if (!shouldShowReminderPopup()) {
      return;
    }

    aiCaptionsPulseTimeoutRef.current = setTimeout(() => {
      setIsAiCaptionButtonPulsating(true);
    }, WAIT_TIME_BEFORE_AI_CAPTION_BUTTON_PULSATES_SECS);

    if (!isMobile) {
      aiCaptionReminderPopupTimeout.current = setTimeout(() => {
        setIsVisibleAiCaptionReminderPopup(true);
        setHasSeenReminderPopup(true);
      }, WAIT_TIME_BEFORE_AI_CAPTION_REMINDER_POPOVER_IS_VISIBLE);
    }
  };

  const abortPulsatingButtonTimer = (): void => {
    if (aiCaptionsPulseTimeoutRef.current) {
      clearTimeout(aiCaptionsPulseTimeoutRef.current);
    }

    if (aiCaptionReminderPopupTimeout.current) {
      clearTimeout(aiCaptionReminderPopupTimeout.current);
    }

    setIsAiCaptionButtonPulsating(false);
    setIsVisibleAiCaptionReminderPopup(false);
  };

  const onEmojiSelected = (newCaption: string): void => {
    if (hasCommonCaption) {
      dispatch(updateCommonCaption(newCaption));
      dispatch(updatePublishingParamAllCaptions({caption: newCaption, isMultiCharacterChange: false}));
    } else {
      dispatch(
        updatePublishingParamCaption({
          idSocialMediaAccount: account.id,
          caption: newCaption,
          isMultiCharacterChange: false,
        })
      );
    }
  };

  useOutsideClick(emojiPickerRef, onEmojiPickerOutsideClick, iconRef.current, () => {
    return isEmojiSelectorVisible;
  });

  const onUseCaptionChange = (isChecked: boolean): void => {
    if (isChecked) {
      setIsShowingConfirmation(true);
    } else {
      dispatch(updateHasCommonCaption(false));
    }
  };

  const enableOverwriteCaptions = (): void => {
    const unifiedCaption = textAreaRef.current?.value;

    if (!hasCommonCaption) {
      dispatch(updateHasCommonCaption(true));
    }

    if (unifiedCaption) {
      dispatch(updateCommonCaption(unifiedCaption));
      dispatch(updatePublishingParamAllCaptions({caption: unifiedCaption, isMultiCharacterChange: true}));
    }
  };

  const triggerTextAreaAnimation = (): void => {
    if (textAreaContainerRef?.current) {
      textAreaContainerRef.current.classList.add(styles.animationFocus);

      setTimeout(() => {
        if (textAreaContainerRef?.current) {
          textAreaContainerRef.current.classList.remove(styles.animationFocus);
        }
      }, 700);
    }
  };

  const onConfirmOverwriteCaptions = (): void => {
    window.dispatchEvent(new CustomEvent(SocialPostCaptionOverwriteEvent));
    dispatch(updateHasCommonCaption(true));
    enableOverwriteCaptions();
    setIsShowingConfirmation(false);
  };

  const onCancelOverwriteCaptions = (): void => {
    setIsShowingConfirmation(false);
  };

  const openUpsellDialogForAutoCaption = (): void => {
    void window.PMW.showPremiumOnlyFeatureDialog(window.PMW.PREMIUM_ONLY_FEATURE_NAME_AUTO_CAPTION).then(() => {
      window.PMW.gtm.trackGA4CustomEvent(GA4EventName.SOCIALWIZARD_AICAPTIONS_UPSELLING);
    });
  };

  const handleError = (): void => {
    if (captionErrors[account.id] === CaptionErrorTypes.COUNT_ERROR) {
      setIsHashtagErrorVisible(false);
      setIsCountErrorVisible(true);
    }

    if (captionErrors[account.id] === CaptionErrorTypes.HASHTAG_ERROR) {
      setIsHashtagErrorVisible(true);
      setIsCountErrorVisible(false);
    }
  };

  const onCaptionChange = (e: ChangeEvent<HTMLTextAreaElement>): void => {
    if (e.target) {
      const caption: string = e.target.value;

      if (hasCommonCaption) {
        dispatch(updateCommonCaption(caption));
        dispatch(
          updatePublishingParamAllCaptions({
            caption,
            isMultiCharacterChange: isCaptionChangeMultiCharacter,
          })
        );
      } else {
        dispatch(
          updatePublishingParamCaption({
            idSocialMediaAccount: account.id,
            caption,
            isMultiCharacterChange: isCaptionChangeMultiCharacter,
          })
        );
      }
    }

    if (isCaptionChangeMultiCharacter) {
      setIsCaptionChangeMultiCharacter(false);
    }
  };

  const onCaptionPaste = (): void => {
    setIsCaptionChangeMultiCharacter(true);
  };

  const onTextAreaClick = (): void => {
    const popOverHasAlreadyBeenShown = window.PMW.getCacheItem(CAPTION_TAGS_POPOVER_ALREADY_SHOWN) as boolean;

    if (!popOverHasAlreadyBeenShown) {
      setShowTagsPopOver(true);
    }
  };

  const autoCaptionsButton = (): ReactElement | null => {
    if (!isSocialMediaWizardAiCaptionsEnabled) {
      return null;
    }

    if (!isUserPremium()) {
      return getPremiumUpsellAiCaptionsButton();
    }

    return getDefaultAutoCaptionButtonWithIntroPopup();
  };

  const onSeenReminderPopup = (): void => {
    setHasSeenReminderPopup(true);
    setIsAiCaptionButtonPulsating(false);
    setIsVisibleAiCaptionReminderPopup(false);
  };

  const commonCaptionCheckboxConfirmation = useMemo((): ReactElement | null => {
    if (accountIdsToPublishTo.length > 1) {
      return (
        <div className="_position-relative">
          <LabelledCheckbox className={styles.captionLabel} onChange={onUseCaptionChange} isChecked={hasCommonCaption} label={window.i18next.t('pmwjs_use_caption_all_posts')} />
          {isShowingConfirmation ? (
            <div className={`flex-v-row ${styles.captionConfirmationBox}`}>
              <div className="flex-row-align-center spacing-m-b-3">
                <Icon icon="icon-info" className={`color-warning-dark ${styles.infoIconSpacer}`} type={IconType.NONE} size={IconSize.SIZE_ICON_20} />
                <Text val={window.i18next.t('pmwjs_are_you_sure_use_caption')} size={TextSize.XSMALL} className={styles.captionConfirmationText} />
              </div>
              <div className="flex-h-row flex-content-end">
                <Button type={Type.SECONDARY} size={Size.SMALL} onClick={onCancelOverwriteCaptions} text={window.i18next.t('pmwjs_cancel')} customClasses="spacing-m-r-2" />
                <Button type={Type.SECONDARY} size={Size.SMALL} onClick={onConfirmOverwriteCaptions} text={window.i18next.t('pmwjs_yes')} />
              </div>
            </div>
          ) : null}
        </div>
      );
    }

    return null;
  }, [isShowingConfirmation, hasCommonCaption]);

  const getCountError = (): ReactElement => {
    const twitterVerifiedCount = 10000;
    const characterLimit = account.isVerifiedTwitterUser ? twitterVerifiedCount : captionCharacterLimits[account.type];

    return (
      <div className="flex-row-align-center spacing-m-t-2">
        <Icon icon="icon-exclamation-triangle" size={IconSize.SIZE_ICON_16} className="spacing-m-r-2 content-danger" />
        <Text className="content-danger" size={TextSize.XSMALL} val={window.i18next.t('pmwjs_caption_long_error', {limit: characterLimit})} />
      </div>
    );
  };

  const getCharCounter = (): ReactElement | null => {
    if (!hasShortCaptionLimit(account.type)) {
      return null;
    }

    const characterLimit = captionCharacterLimits[account.type];

    return (
      <Text
        className={`${styles.charCounter} ${isCountErrorVisible ? styles.captionError : ''}`}
        size={TextSize.XXSMALL}
        type={TextType.BODY}
        val={`${captionCharCount} / ${characterLimit}`}
      />
    );
  };

  const getHashtagError = (): ReactElement => {
    return (
      <div className="flex-row-align-center spacing-m-t-2">
        <Icon icon="icon-exclamation-triangle" size={IconSize.SIZE_ICON_16} className="spacing-m-r-2 content-danger" />
        <Text className="content-danger" size={TextSize.XSMALL} val={window.i18next.t('pmwjs_hashtag_error')} />
      </div>
    );
  };

  const emojiPicker = useMemo((): ReactElement | null => {
    return (
      <div ref={emojiPickerRef} className={`${styles.emojiSelector} ${isEmojiSelectorVisible ? styles.visible : ''}`}>
        <EmojiPickerInput
          textAreaRef={textAreaRef}
          onEmojiSelectCallback={(newText) => {
            onEmojiSelected(newText);
          }}
        />
      </div>
    );
  }, [isEmojiSelectorVisible]);

  const getPremiumUpsellAiCaptionsButton = (): ReactElement => {
    return (
      <div className={styles.autoCaptionButtonContainer}>
        <AiButton text={window.i18next.t('pmwjs_ai_captions')} variant={AIButtonVariant.PREMIUM} size={Size.SMALL} onClick={openUpsellDialogForAutoCaption} />
      </div>
    );
  };

  const getAutoCaptionIntroPopup = (): ReactElement | null => {
    if (!shouldShowIntroPopup()) {
      return null;
    }

    return (
      <IntroPopup
        userHasSeenCallbackOnClose={afterCloseIntroPopup}
        title={window.i18next.t('pmwjs_say_goodbye_to_writers_block')}
        content={window.i18next.t('pmwjs_ai_caption_intro_text')}
        assetUrl={getAssetUrl('ai-captions-illustration.png')}
        leftOffset={70}
        shouldFlip={false}
        shouldShift
        primaryFooterAction={
          <Button
            text={window.i18next.t('pmwjs_try_now')}
            type={Type.PRIMARY}
            size={Size.XSMALL}
            customClasses={styles.secondaryGradeintColorBackground}
            onClick={() => {
              onAiCaptionClick(true);
            }}
          />
        }
      />
    );
  };

  const getAiCaptionReminderPopup = (): ReactElement | null => {
    if (isUserEligibleToSeeTagsPopOver) {
      return null;
    }

    if (!isVisibleAiCaptionReminderPopup) {
      return null;
    }

    return (
      <IntroPopup
        userHasSeenCallbackOnClose={onSeenReminderPopup}
        title={window.i18next.t('pmwjs_let_ai_do_the_writing_for_you')}
        content={window.i18next.t('pmwjs_ai_caption_reminder_popup_text')}
        assetUrl={getAssetUrl('ai-caption-reminder-graphic.png')}
        mediaWidth={320}
        leftOffset={70}
        hasSmoothFadeIn
        shouldShift
        shouldFlip={false}
      />
    );
  };

  const getDefaultAutoCaptionButtonWithIntroPopup = (): ReactElement => {
    return (
      <div className={styles.autoCaptionButtonContainer}>
        <AiButton
          buttonRef={aiCaptionButtonRef}
          text={window.i18next.t('pmwjs_help_me_write')}
          onClick={onAiCaptionClick as VoidFunction}
          variant={AIButtonVariant.SUBDUED}
          shape={AIButtonShape.ROUND}
          isPulsating={isAiCaptionButtonPulsating}
          size={Size.XSMALL}
          customClasses="spacing-p-t-2 spacing-p-b-2"
          icon="icon-ai-caption"
        />
        {getAutoCaptionIntroPopup()}
        {getAiCaptionReminderPopup()}
        {renderCaptionWriterPopover()}
      </div>
    );
  };

  const renderCaptionWriterPopover = (): ReactElement | null => {
    if (!isCaptionWriterOpen || !postId) {
      return null;
    }

    return (
      <CaptionWriterPopoverView
        isOpen={isCaptionWriterOpen}
        referenceElementRef={aiCaptionButtonRef}
        accountId={account.id}
        postId={postId}
        onClose={onCaptionWriterClose}
        onContentSelected={chooseCaption}
      />
    );
  };

  const renderTextAreaWithOverlay = (): ReactElement => {
    return (
      <div className={styles.textAreaWrapper}>
        <div
          ref={textAreaOverlayRef}
          className={`${styles.textArea} ${styles.textAreaOverlay} ${isCountErrorVisible ? styles.captionError : ''}`}
          contentEditable={false}
          aria-hidden="true"
          dangerouslySetInnerHTML={{__html: currentCaptionAsHtml}}
        />
        <textarea
          onPaste={onCaptionPaste}
          onClick={onTextAreaClick}
          ref={textAreaRef}
          className={`${styles.textArea} ${isCountErrorVisible ? styles.captionError : ''}`}
          placeholder={getCaptionPlaceholder()}
          onChange={onCaptionChange}
          value={getCurrentCaption()}
        />
      </div>
    );
  };

  const renderSocialPostCaption = (): ReactElement => {
    return (
      <motion.div
        key="social-caption-animated-container"
        initial={{opacity: 0, height: AUTO_CAPTION_BOX_HEIGHT}}
        animate={{
          opacity: 1,
          height: CAPTION_TEXT_BOX_HEIGHT,
          transition: {
            opacity: {duration: 0.3},
            height: {duration: 0.3},
          },
        }}
        exit={{
          opacity: 0,
          height: AUTO_CAPTION_BOX_HEIGHT,
          transition: {
            opacity: {duration: 0.3},
            height: {duration: 1},
          },
        }}
        className={styles.captionContainer}
      >
        <div
          ref={textAreaContainerRef}
          className={`flex-v-row flex-content-space-between _full-height ${styles.textAreaContainer} ${isCountErrorVisible ? styles.captionError : ''}`}
          onFocus={startPulsatingButtonTimer}
          onBlur={abortPulsatingButtonTimer}
        >
          {renderTextAreaWithOverlay()}
          <div className={`flex-row-justify-between flex-align-items-center spacing-p-2 ${styles.textAreaContainerFooter}`}>
            {emojiPicker}
            <div className={styles.charCounterAndAIButtonContainer}>
              {getCharCounter()}
              {autoCaptionsButton()}
            </div>
          </div>
        </div>
        {commonCaptionCheckboxConfirmation}
        {isCountErrorVisible ? getCountError() : null}
        {isHashtagErrorVisible ? getHashtagError() : null}
      </motion.div>
    );
  };

  const getEmojiFromLangString = (string?: string): string | undefined => {
    return string?.slice(0, 2);
  };

  const getTextFromLangString = (string?: string): string | undefined => {
    return string?.slice(2);
  };

  const renderAutoCaptionGeneration = (): ReactElement => {
    return (
      <div className={`${styles.title} ${styles.aiLoader}`}>
        <div>
          <span>{getEmojiFromLangString(autoGenerationLoadingCaption)}</span>
          <span className={styles.aiFontColor}>{getTextFromLangString(autoGenerationLoadingCaption)}</span>
        </div>
      </div>
    ) as ReactElement;
  };

  const getContent = (): ReactElement | null => {
    if (areCaptionsBeingAutoGenerated) {
      return renderAutoCaptionGeneration();
    }

    return renderSocialPostCaption();
  };

  const handlePopOverClose = (): void => {
    setShowTagsPopOver(false);
    void window.PMW.writeLocal('socialmedia/incrementUserHasSeenCaptionTagsPopoverCount');
  };

  const captionTagsPopOver = (): ReactElement => {
    const captionTagsHtml = window.i18next.t('pmwjs_caption_tags_info', {
      mentions: `<span class="${styles.blueText}">@mentions</span>`,
      hashtags: `<span class="${styles.blueText}">#hashtags</span>`,
    });

    return (
      <Popover
        referenceElementRef={textAreaContainerRef}
        popoverCloseFunction={() => {
          handlePopOverClose();
        }}
        alignment={PopoverDefaultAlignment.BOTTOM_START}
        popoverOffset={-180}
        hasArrow
        isPositionFixed={false}
        className={`border-s-standard ${styles.popOver}`}
        disableAutoPositioning={true}
      >
        <div className={`flex-v-row spacing-p-3`}>
          <div className={`flex-row-justify-between flex-align-items-center spacing-m-b-3`}>
            <Text size={TextSize.SMALL} bold val={window.i18next.t('pmwjs_add_tags_easily')} />
            <Icon onClick={handlePopOverClose} icon="icon-close" size={IconSize.SIZE_ICON_16} type={IconType.GHOST_UNCOLORED} shape={IconShape.SQUARE} isUsedAsButton />
          </div>
          <div>
            <img src={getAssetUrl('caption-tags-popover-image.webp')} alt={'captions tooltip image'} />
          </div>
          <Text size={TextSize.XSMALL} className={'spacing-m-t-2'} val={captionTagsHtml} dangerouslySetInnerHTML />
        </div>
      </Popover>
    );
  };

  const renderCaptionTagsPopOver = (): ReactElement | null => {
    if (showTagsPopOver && isUserEligibleToSeeTagsPopOver) {
      window.PMW.setCacheItem(CAPTION_TAGS_POPOVER_ALREADY_SHOWN, true, window.PMW.CACHE_EXPIRY_HALF_HOUR);
      return captionTagsPopOver();
    }

    return null;
  };

  return (
    <div
      ref={socialPostCaptionContainerRef}
      className={`${styles.socialCaptionContainer} social-caption-container`}
      style={{height: CAPTION_CONTAINER_TEXTBOX_HEIGHT}}
      key={`${account.id} ${platformSelection}`}
    >
      <AnimatePresence mode="popLayout" initial={false}>
        {getContent()}
      </AnimatePresence>

      {renderCaptionTagsPopOver()}
    </div>
  );
}

export default React.memo(SocialPostCaption);
